import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import { hoursCountToTimeString, timeStringToHoursCount } from '../../../util/util';
import { getDailyScheduleReport } from '../../../services/reports.service';

const useDailyReport = (props) => {
  const { year, month, day, version, atcu } = props;
  const { t } = useTranslation();

  const getReportData = async () => {
    try {
      const date = new Date(parseInt(year, 10), (parseInt(month, 10) - 1), (parseInt(day, 10) + 1))?.toISOString().slice(0, 10);
      const res = await getDailyScheduleReport(date, atcu);
      return res?.data;
    } catch (e) {
      console.log(e);
    }
  };

  const addLeadingZero = (num) => {
    return num < 10 ? '0' + num : num.toString();
  };

  const dateString = addLeadingZero(day) + '. ' + addLeadingZero(month) + '. ' + year + '.';

  const mapShiftsExportData = (data) => {
    const mappedData = [];
    data?.forEach((shift) => {
      const shiftRows = [
        [' '],
        ['Datum: ' + dateString],
        [' '],
        ['Ime i prezime', 'Smjena']
      ];
      const startingTime = timeStringToHoursCount(shift?.shift?.timeFrom, false);
      const endingTime = timeStringToHoursCount(shift?.shift?.timeTo, true);
      for (let i = startingTime; i < (startingTime + shift?.shift?.duration); i += 0.5) {
        const timeInt = i < 24 ? i : (i - 24);
        shiftRows?.[3]?.push(hoursCountToTimeString(timeInt, false));
      }
      shift?.rows?.forEach((row) => {
        const hasAtleastOneOnDutyObligation = row?.items?.find((el) => el?.isOnDuty === true);
        const isShiftManager = row?.isShiftManager;
        const isSatco = row?.isSatco;
        if (
          (!isShiftManager && !isSatco && row?.items?.length > 0)
          || (isShiftManager || isSatco)
        ) {
          const rowName = isShiftManager ? 'ŠS' : (isSatco ? 'SATCO' : row?.shift?.name);
          const controllerName = row?.firstName + ' ' + row?.lastName;
          const rowData = [
            controllerName,
            rowName
          ];
            for (let i = startingTime; i < (startingTime + shift?.shift?.duration); i += 0.5) {
              const timeInt = i;
              // FIND WHAT CONTROLLER IS DOING ON THAT HALF-HOUR
              const dutyOnThatTime = row?.items?.find((item) => {
                let itemStartTime = timeStringToHoursCount(item?.timeFrom, false);
                let itemEndTime = timeStringToHoursCount(item?.timeTo, true);
                if (startingTime > endingTime) {
                  itemStartTime = itemStartTime < 10 ? itemStartTime + 24 : itemStartTime;
                  itemEndTime = itemEndTime < 10 ? itemEndTime + 24 : itemEndTime;

                  return timeInt >= itemStartTime && timeInt < itemEndTime;
                }
                return timeInt >= itemStartTime && timeInt < itemEndTime;
              });
              if (dutyOnThatTime?.label.startsWith('ŠS/KL')) {
                dutyOnThatTime.label = dutyOnThatTime?.label.substring(0, 3) + dutyOnThatTime?.label.substring(6);
                rowData.push(dutyOnThatTime ? dutyOnThatTime : null);
              } else if (hasAtleastOneOnDutyObligation) {
                rowData.push(dutyOnThatTime ? (dutyOnThatTime?.label !== 'P' ? dutyOnThatTime : null) : null);
              }
            }
          if (hasAtleastOneOnDutyObligation) shiftRows.push(rowData);
        }
      });
      if (shiftRows?.length > 4) {
        mappedData.push(shiftRows);
      }
    });
    return mappedData;
  };

  const getAlphabetLetterByOrdinalNumber = (ordinal) => {
    if (!Number.isInteger(ordinal) || ordinal < 1 || ordinal > 26) {
      return null;
    }

    return String.fromCharCode(64 + ordinal);
  };

  const getSectorColor = (sectorName) => {
    switch (sectorName) {
      case 'L':
      case 'LU':
        return '#03A802'; // Green
      case 'U':
        return '#FF7E00'; // Orange
      case 'H':
      case 'HT':
      case 'UHT':
      case 'LUHT':
      case 'LUHTS':
      case 'HTS':
      case 'UHTS':
        return '#FF0000'; // Red
      case 'T':
      case 'TS':
        return '#0070C0'; // Blue
      case 'S':
        return '#fcba03'; // Yellow
      default:
        return '#000000'; // Black
    }
  };

  const getDailyReportXlsxFile = async () => {
    const reportData = await getReportData();
    const mappedData = mapShiftsExportData(reportData);

    XlsxPopulate.fromBlankAsync().then(async (workbook) => {
      mappedData?.forEach((mappedShift, shiftIndex) => {
        const shiftLabels = {
          0: t('dailySchedule.morning'),
          1: t('dailySchedule.noon'),
          2: t('dailySchedule.night')
        }
        let sheet;
        if (shiftIndex === 0) {
          sheet = workbook.sheet(shiftIndex).name(shiftLabels[shiftIndex])
        } else {
          sheet = workbook.addSheet(shiftLabels[shiftIndex]);
        }

        sheet.range('A1:D1').merged(true);
        sheet.range('E1:I1').merged(true);
        sheet.range('L1:M1').merged(true);
        sheet.range('P1:Q1').merged(true);
        sheet.row(1).height(45);

        sheet.cell('E1').value('Oznaka:ATC.FORM.124');
        sheet.cell('L1').value('Verzija:' + version + '.0');
        sheet.cell('P1').value('ATC.UPU.019');

        sheet.range('A2:Q2').merged(true);
        sheet.range('A2:Q2').style({ bold: true });
        sheet.cell('I2').value('DNEVNI RASPORED');
        sheet.cell('A2').style({ bold: true });

        const startRow = sheet.usedRange()._numRows + 1;
        mappedShift?.forEach((tableRow, index) => { // LOOP THROUGH EACH SHIFT TO MAKE ITS TABLE
          const isLastRow = index === mappedShift?.length - 1;
          const rowNumberToInsert = startRow + index + 1;
          if (index < 4) {
            if (index === 3) {
              tableRow?.forEach((rowCell, cellIndex) => { // WHERE HALF-HOUR TITLE CELLS START (SHIFT TABLE HEADER)
                sheet.row(index + 4).height(30);
                const cellToInsert = getAlphabetLetterByOrdinalNumber(cellIndex + 1) + rowNumberToInsert.toString();
                const isFirstCellInRow = cellIndex === 0;
                const isLastCellInRow = cellIndex === tableRow?.length - 1;
                const isEven = cellIndex % 2 === 0;
                sheet.cell(cellToInsert).value(rowCell).style({
                  topBorderStyle: 'medium',
                  bottomBorderStyle: 'medium',
                  leftBorderStyle: (isFirstCellInRow || isEven) ? 'medium' : 'thin',
                  rightBorderStyle: isLastCellInRow || isFirstCellInRow ? 'medium' : (!isEven ? 'medium' : 'thin')
                })
              })
            } else {
              sheet.cell('A' + rowNumberToInsert).value([tableRow]);
            }
          } else { // WHERE REAL SHIFT TABLE ROWS ACTUALLY START
            sheet.row(index + 4).height(30);
            const evenRow = index % 2 === 0;
            const offDutyCellsToMerge = [];
            let managerCellsToMerge = [];
            tableRow?.forEach((rowCell, cellIndex) => {
              const cellToInsert = getAlphabetLetterByOrdinalNumber(cellIndex + 1) + rowNumberToInsert.toString();
              const nextCell = tableRow?.[cellIndex + 1];
              const isFirstCell = cellIndex === 0;
              const isEven = cellIndex % 2 === 0;
              const isLastCell = cellIndex === tableRow?.length - 1;
              const isManagerCell = rowCell?.label?.includes('ŠS') || rowCell?.label?.includes('SATCO') || rowCell?.label?.includes('FMP');
              const nextCellIsManager = nextCell?.label?.includes('ŠS') || nextCell?.label?.includes('SATCO') || nextCell?.label?.includes('FMP');
              if (cellIndex === 0 || cellIndex === 1) { // REAL ROW SHIFT NAME CELL
                sheet.cell(cellToInsert).value(rowCell).style({
                  bottomBorderStyle: isLastRow ? 'medium' : 'thin',
                  rightBorderStyle: 'medium'
                });
              } else {
                if (isManagerCell && nextCellIsManager) {
                  managerCellsToMerge.push(cellToInsert);
                } else if (managerCellsToMerge?.length > 1 && isManagerCell && (!nextCellIsManager || !nextCell)) {
                  sheet.range(managerCellsToMerge?.[0] + ':' + cellToInsert).merged(true).style({
                    bold: true,
                    bottomBorderStyle: isLastRow ? 'medium' : 'thin',
                    leftBorderStyle: isFirstCell ? 'medium' : (!managerCellsToMerge?.length % 2 === 0 ? 'medium' : 'thin'),
                    rightBorderStyle: isLastCell || isFirstCell ? 'medium' : (!isEven ? 'thin' : 'medium')
                  })
                } else if (!isManagerCell) {
                  managerCellsToMerge = [];
                }
                if ((rowCell?.label && rowCell?.isOnDuty === false) && (nextCell?.label && nextCell?.isOnDuty === false)) {
                  offDutyCellsToMerge.push(cellToInsert);
                } else if (
                    (rowCell?.label && rowCell?.isOnDuty === false)
                    && ((nextCell?.label && nextCell?.isOnDuty === true) || !nextCell)) {
                  if (offDutyCellsToMerge?.length > 0) {
                    sheet.range(offDutyCellsToMerge?.[0] + ':' + cellToInsert).merged(true).style({
                      fill: 'bfbfbf',
                      bold: true,
                      bottomBorderStyle: isLastRow ? 'medium' : 'thin',
                      leftBorderStyle: isFirstCell ? 'medium' : (!offDutyCellsToMerge?.length % 2 === 0 ? 'thin' : 'medium'),
                      rightBorderStyle: isLastCell || isFirstCell ? 'medium' : (!isEven ? 'thin' : 'medium')
                    });
                    sheet.cell(offDutyCellsToMerge?.[0]).value(' ');
                    offDutyCellsToMerge.splice(0, offDutyCellsToMerge?.length);
                  } else {
                    sheet.cell(cellToInsert).value(' ').style({
                      fill: 'bfbfbf',
                      bold: true,
                      bottomBorderStyle: isLastRow ? 'medium' : 'thin',
                      leftBorderStyle: isFirstCell ? 'medium' : (!isEven ? 'medium' : 'thin'),
                      rightBorderStyle: isLastCell ? 'medium' : (!isEven ? 'thin' : 'medium')
                    });
                  }
                }
                const sectorName = rowCell?.label?.split(' ')?.length > 0 ? rowCell?.label?.split(' ')?.[0] : null;
                let color = 'black';
                if (sectorName) color = getSectorColor(sectorName)?.substring(1);
                sheet.cell(cellToInsert).value(rowCell?.label).style({
                  fontColor: color,
                  bold: true,
                  fill: evenRow ? 'f2f2f2' : 'ffffff',
                  leftBorderStyle: isFirstCell ? 'medium' : (!isEven ? 'thin' : 'medium'),
                  rightBorderStyle: isLastCell ? 'medium' : (!isEven ? 'medium' : 'thin'),
                  bottomBorderStyle: isLastRow ? 'medium' : 'thin',
                });
              }
            });
          }
          if (index === 1) { // SHIT NAME CELL BEFORE THE MAIN TABLE
            const shiftLabels = ['J', 'P', 'N'];
            sheet.range('I' + rowNumberToInsert + ':J' + rowNumberToInsert).merged(true);
            sheet.range('I' + rowNumberToInsert + ':J' + rowNumberToInsert).style({
              bold: true,
              border: true,
              borderStyle: 'medium',
            })
            sheet.row(index + 4).height(30);
            sheet.cell('I' + rowNumberToInsert).value(shiftLabels?.[shiftIndex]);
          }
        });

        sheet.usedRange().style({
          horizontalAlignment: 'center',
          verticalAlignment: 'center',
        });

        sheet.column('A').width(20);
        sheet.column('A').style({ bold: true });
        sheet.cell('A5').style({
          horizontalAlignment: 'left'
        })

        const signatureRow = (sheet.usedRange()._numRows + 2).toString();
        sheet.range('M' + signatureRow + ':N' + signatureRow).merged(true).style({
          horizontalAlignment: 'left'
        });
        sheet.row(signatureRow - 1).height(30);
        sheet.row(signatureRow).height(30);
        sheet.column('B').style({ bold: true })
        sheet.cell('M' + signatureRow).value('Šef smjene:');
        sheet.range('O' + signatureRow + ':Q' + signatureRow).style({
          bottomBorder: true
        });
      });

      return workbook.outputAsync().then((res) => {
        const fileName = `${t('main.dailyPlan')} - ${atcu} - ${day}-${t('monthlyPlan.' + month)}-${year}-${t('dailySchedule.version')}-${version}`;
        saveAs(res, fileName, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      })
    });
  };

  return {
    getDailyReportXlsxFile
  };
};

export default useDailyReport;
